import React, { Component } from 'react';
import { routes } from 'constants/config';
import { Card } from 'ui/';
import request from 'utils/request_ajax';
import Text, { TEXT_VARIANTS } from 'components/ui/Typography/Text';
import FeatureFlag from 'components/shared/FeatureFlag';
import classNames from 'classnames';
import AttendanceStatus from 'components/InPersonTraining/EventRegistrationOverview/AttendanceStatus';
import Loading from 'components/shared/Loading';
import { RegistrationCapText } from 'components/InPersonTraining/shared/SessionDetails';
import {
  getSeriesRegistrationCap,
  getSeriesTotalRegistered,
} from 'components/InPersonTraining/EventsDashboard/utils';
import EventNotFoundGraphic from 'components/shared/EventNotFoundGraphic';
import Header from './Header';
import EventsDashboardBackLink from './EventsDashboardBackLink';
import AttendanceForm from './AttendanceForm';
import SessionListing from './SessionListing';

class EventRegistration extends Component {
  state = {
    loading: true,
    event: null,
    sessionWithRelevantAttendance: null,
    sessions: [],
    attendance: null,
    selectedSessionId: null,
    changingAttendance: false,
    meta: {
      policies: {
        can_change_attendance: null,
      },
      currentUser: {
        id: null,
      },
    },
    type: {
      past_event: null,
      multi_session: null,
    },
  };

  getEventRegistration = async eventId => {
    const [eventRequest, sessionsRequest, guestsRequest] = await Promise.all([
      fetch(routes.TRAINING.EVENT(eventId)).then(value => value.json()),
      fetch(routes.TRAINING.EVENT_SESSIONS(eventId)).then(value =>
        value.json(),
      ),
      fetch(routes.TRAINING.MY_EVENT_GUESTS(eventId)).then(value =>
        value.json(),
      ),
    ]);

    // Single session selectedSessionId starts as only available session id
    // Multi session selectedSessionId starts as default null value

    const { sessions } = sessionsRequest;
    const isMultiSession = sessions.length > 1;
    const selectedSessionId = isMultiSession ? null : sessions?.[0]?.id;

    const currentUser = guestsRequest.filteredUser;
    const guestSessions = guestsRequest.data[0]?.sessions;
    const firstAttendanceRecord = guestSessions?.[0];

    let canChangeAttendance = sessions[0]?.canChangeAttendance;

    if (isMultiSession) {
      if (eventRequest.rsvpType === 'single') {
        canChangeAttendance = sessions.some(session => {
          return session.canChangeAttendance;
        });
      } else if (eventRequest.rsvpType === 'all') {
        canChangeAttendance = sessions.every(session => {
          return session.canChangeAttendance;
        });
      }
    }

    const sessionsWithAttendance = sessions.map(session => {
      const thisGuestSession = guestSessions?.find(guestSession => {
        return guestSession.id === session.id;
      });

      let status = 'awaiting_reply';
      if (thisGuestSession?.attendanceStatus === 'attended') {
        status = 'did_attend';
      } else if (thisGuestSession?.attendanceStatus === 'did_not_attend') {
        status = 'did_not_attend';
      } else if (
        thisGuestSession?.attendanceStatus === 'unconfirmed' &&
        thisGuestSession?.attendanceTaken
      ) {
        status = 'did_not_attend';
      } else if (thisGuestSession?.rsvpStatus) {
        status = thisGuestSession?.rsvpStatus;
      }

      let { atCapacity } = session;
      if (session.maxAttendees) {
        if (status === 'did_attend') {
          atCapacity = false;
        }
      }

      return {
        ...session,
        type: {
          at_capacity: atCapacity,
        },
        attendance: {
          session_id: session.id,
          status,
          rsvp_status: thisGuestSession?.rsvpStatus,
          did_attend: status === 'did_attend',
        },
      };
    });

    const pastEvent = this.isPastEvent(sessionsWithAttendance);

    this.setState({
      selectedSessionId,
      sessionWithRelevantAttendance: this.relevantSession(
        sessionsWithAttendance,
      ),
      event: {
        id: eventRequest.id,
        name: eventRequest.name,
        description: eventRequest.description,
        rsvp_type: eventRequest.rsvpType,
        is_mandatory: eventRequest.isMandatory,
      },
      sessions: sessionsWithAttendance,
      attendance: {
        ...firstAttendanceRecord,
        type: {
          past_event: pastEvent,
        },
      },
      type: {
        multi_session: isMultiSession,
        past_event: pastEvent,
      },
      meta: {
        policies: {
          can_change_attendance: canChangeAttendance,
        },
        currentUser: {
          id: currentUser,
        },
      },
      changingAttendance: false,
      loading: false,
    });
  };

  componentDidMount() {
    const eventId = window.location.href.match(
      /in_person_training\/attendee\/events\/(\d+)\//,
    )[1];

    this.getEventRegistration(eventId);
  }

  onSessionClick = selectedSession => {
    const { meta, type } = this.state;

    this.setState({
      selectedSessionId: selectedSession.id,
      type: {
        ...type,
        past_event: moment(selectedSession.startTime) < Date.now(),
      },
      meta: {
        ...meta,
        policies: {
          can_change_attendance: selectedSession.canChangeAttendance,
        },
      },
    });
  };

  isEventAtCapacity = () => {
    if (this.state.event?.rsvp_type === 'all') {
      return this.state.sessions.some(session => session.type.at_capacity);
    }
    return this.state.sessions.every(session => session.type.at_capacity);
  };

  hasPositiveStatus = status => {
    return ['did_attend', 'will_attend'].includes(status);
  };

  hasNegativeStatus = status => {
    return ['did_not_attend', 'will_not_attend'].includes(status);
  };

  awaitingReply = status => {
    return [null, 'awaiting_reply'].includes(status);
  };

  relevantSession = sessions => {
    let relevantSession;

    // Find the sessions with a positive status
    const positiveStatuses = sessions?.filter(session =>
      this.hasPositiveStatus(session.attendance?.status),
    );

    let positiveStatus = positiveStatuses?.[0];

    // Prefer the session with the latest start time
    if (positiveStatuses && positiveStatuses.length > 1) {
      positiveStatus = positiveStatuses.reduce((prev, curr) => {
        return moment(prev.startTime) > moment(curr.startTime) ? prev : curr;
      });
    }

    if (positiveStatus) {
      relevantSession = positiveStatus;
    } else {
      relevantSession =
        sessions?.find(session =>
          this.hasNegativeStatus(session.attendance?.status),
        ) || sessions?.[0];
    }
    return relevantSession;
  };

  isPastEvent = sessions => {
    const { selectedSessionId } = this.state;

    // For a multi-session event, if rsvp_type 'all then
    // we may have a selectedSessionId, and if we do then
    // use it's past status
    if (selectedSessionId) {
      const selectedSessionStartTime = sessions.find(
        session => session.id === selectedSessionId,
      ).startTime;
      return moment(selectedSessionStartTime) < Date.now();
    }

    // If we have session(s) with positive status attendance
    // then use those session's past status
    const sessionWithPositiveStatusAttendance = sessions?.filter(session =>
      ['did_attend', 'will_attend'].includes(session.attendance?.status),
    );
    if (
      sessionWithPositiveStatusAttendance &&
      sessionWithPositiveStatusAttendance.length
    ) {
      return sessionWithPositiveStatusAttendance?.every(session => {
        return moment(session.startTime) < Date.now();
      });
    }

    // Otherwise, we're working with a past context if all sessions are in the past
    return sessions?.every(session => {
      return moment(session.startTime) < Date.now();
    });
  };

  hasRsvpInfo = () => {
    const { changingAttendance, sessionWithRelevantAttendance } = this.state;
    return (
      !this.awaitingReply(sessionWithRelevantAttendance?.attendance?.status) &&
      !changingAttendance
    );
  };

  showRsvpAllAtCapacity = () => {
    const positiveStatus = this.hasPositiveStatus(
      this.state.sessionWithRelevantAttendance?.attendance?.status,
    );
    return (
      this.isEventAtCapacity() &&
      !positiveStatus &&
      this.state.event?.rsvp_type === 'all'
    );
  };

  showAttendanceStatus = () => {
    if (this.state.loading) {
      return false;
    }

    const pastEvent = this.state.type.past_event;
    const canChangeAttendance = this.state.meta.policies.can_change_attendance;

    const pastEventUneditable = pastEvent && !canChangeAttendance;

    return (
      this.hasRsvpInfo() || pastEventUneditable || this.isEventAtCapacity()
    );
  };

  showStatusInFooter = () => {
    const rsvpSingleWithPositiveStatus =
      this.state.event?.rsvp_type === 'single' &&
      this.hasPositiveStatus(this.relevantSession()?.attendance?.status);
    return (
      this.showAttendanceStatus() &&
      this.hasMultipleSessions() &&
      !rsvpSingleWithPositiveStatus &&
      !this.showRsvpAllAtCapacity()
    );
  };

  showAttendanceForm = () => {
    if (this.state.loading) {
      return false;
    }

    const canChangeAttendance = this.state.meta.policies.can_change_attendance;
    const pastEvent = this.state.type.past_event;

    if (canChangeAttendance) {
      return true;
    }

    return !pastEvent;
  };

  onChangeAttendance = () => {
    this.setState({
      changingAttendance: true,
    });
  };

  onUpdateAttendance = attendanceStatus => {
    const { event, meta, selectedSessionId, type } = this.state;
    const status = type.past_event
      ? { attendanceStatus: attendanceStatus ? 'did_attend' : 'did_not_attend' }
      : { rsvpStatus: attendanceStatus ? 'will_attend' : 'will_not_attend' };
    const url =
      event.rsvp_type === 'single' && selectedSessionId
        ? routes.TRAINING.SESSION_SET_ATTENDANCE(event.id, selectedSessionId)
        : routes.TRAINING.EVENT_SET_RSVP(event.id);
    request
      .put(url, {
        ...status,
        userId: meta.currentUser.id,
        createdFrom: null,
      })
      .then(payload => {
        if (payload.data) {
          this.getEventRegistration(event.id);
        }
        this.setState({
          selectedSessionId: null,
        });
      });
  };

  // @todo Stub until we begin saving data
  displayRsvpConfirmation = () => {
    return false;
  };

  // @todo Stub until we begin saving data
  displayRsvpRejection = () => {
    return false;
  };

  showRsvpButtons = () => {
    const hasAttendance = this.hasRsvpInfo();
    const { meta, changingAttendance } = this.state;
    const { past_event: pastEvent, multi_session: multiSession } =
      this.state.type;

    if (multiSession && !hasAttendance && !this.isEventAtCapacity()) {
      return true;
    }

    const neverRegistered =
      !hasAttendance && (!pastEvent || meta.policies.can_change_attendance);

    return (
      (neverRegistered || changingAttendance) &&
      (!this.isEventAtCapacity() ||
        this.state.sessionWithRelevantAttendance?.attendance?.rsvp_status ===
          'will_attend')
    );
  };

  showAddToCalendar = () => {
    const { sessionWithRelevantAttendance, type } = this.state;
    return (
      sessionWithRelevantAttendance?.attendance?.status === 'will_attend' &&
      !type.past_event
    );
  };

  hasMultipleSessions = () => {
    return this.state.type.multi_session;
  };

  unconfirmedRsvpForFutureMultisession = () => {
    return (
      this.hasMultipleSessions() &&
      !this.state.type.past_event &&
      !this.hasRsvpInfo()
    );
  };

  render() {
    const {
      loading,
      event,
      sessions,
      selectedSessionId,
      sessionWithRelevantAttendance,
      attendance,
      changingAttendance,
    } = this.state;

    const totalSessions = sessions.length;
    const positiveRsvp = this.hasPositiveStatus(
      sessionWithRelevantAttendance?.attendance?.status,
    );
    const isSeries = this.hasMultipleSessions() && event?.rsvp_type === 'all';
    const seriesRegistrationCap =
      isSeries && getSeriesRegistrationCap(sessions);
    const seriesTotalRegistered = getSeriesTotalRegistered(sessions);
    const isUnconfirmedRsvpForFutureMultisession =
      this.unconfirmedRsvpForFutureMultisession();
    const archivedOrDeleted =
      !event || !!event?.archived_at || !!event?.deleted_at;
    const showSessionAtCapacityNotice =
      this.isEventAtCapacity() && !positiveRsvp;

    return (
      <div className="EventRegistration">
        <Loading loading={loading}>
          {archivedOrDeleted && <EventNotFoundGraphic isLearner />}
          {!archivedOrDeleted && (
            <>
              <EventsDashboardBackLink />
              <Header {...event} />
              {seriesRegistrationCap && (
                <div className="EventRegistrationCap">
                  <Text variant={TEXT_VARIANTS.BODY_LIGHT}>
                    {`${t(
                      'in_person_training.event_overview.registration_cap.registration_capacity',
                    )} `}
                    <RegistrationCapText
                      maxAttendees={seriesRegistrationCap}
                      totalRegistered={seriesTotalRegistered}
                    />
                  </Text>
                </div>
              )}
              <Card>
                <Card.Content padding="large">
                  <div className="d-f fd-c">
                    <Text
                      className={classNames({
                        'EventRegistration-details': isSeries,
                        'mb-16':
                          !isUnconfirmedRsvpForFutureMultisession &&
                          event?.rsvp_type === 'single',
                      })}
                      variant={TEXT_VARIANTS.SUBHEADER_BOLD}
                    >
                      {t('in_person_training.event_details')}
                    </Text>

                    {isUnconfirmedRsvpForFutureMultisession &&
                      event?.rsvp_type === 'single' && (
                        <div className="MySessionListDescription mb-20">
                          <Text variant={TEXT_VARIANTS.BODY_LIGHT}>
                            {t(
                              'in_person_training.events.register.select_a_session',
                            )}
                          </Text>
                          <Text variant={TEXT_VARIANTS.BODY_LIGHT}>
                            {t('in_person_training.events.number_of_sessions', {
                              count: totalSessions,
                            })}
                          </Text>
                        </div>
                      )}

                    {isSeries && (
                      <div className="mt-8 mb-20 ml-16 d-f jc-sb">
                        <Text variant={TEXT_VARIANTS.BODY_LIGHT}>
                          {t(
                            'in_person_training.events.register.includes_sessions',
                            { count: sessions.length },
                          )}
                        </Text>
                      </div>
                    )}

                    <FeatureFlag
                      features={['readiness-enable-multisession-events']}
                    >
                      {this.displayRsvpConfirmation() && (
                        <Text
                          className="mt-16 d-b"
                          variant={TEXT_VARIANTS.BODY_LIGHT}
                        >
                          {t(
                            'in_person_training.rsvp_status.confirmation_message',
                          )}
                        </Text>
                      )}
                    </FeatureFlag>

                    {sessionWithRelevantAttendance && (
                      <div>
                        <SessionListing
                          sessions={sessions}
                          selectedSessionId={selectedSessionId}
                          handleSessionClick={this.onSessionClick}
                          hasMultipleSessions={this.hasMultipleSessions()}
                          attendance={sessionWithRelevantAttendance.attendance}
                          currentSession={sessionWithRelevantAttendance}
                          event={event}
                          changingAttendance={changingAttendance}
                          canChangeAttendance={
                            this.state.meta.policies.can_change_attendance
                          }
                          customLinkText={t(
                            'in_person_training.event_overview.click_here_to_join',
                          )}
                        />
                      </div>
                    )}

                    {this.showRsvpAllAtCapacity() && isSeries && (
                      <div>
                        <hr className="mt-24 mb-24" />
                      </div>
                    )}

                    {this.showAttendanceStatus() &&
                      !this.showStatusInFooter() &&
                      sessionWithRelevantAttendance &&
                      !changingAttendance && (
                        <div
                          className={classNames({
                            'mt-12': !this.hasMultipleSessions(),
                          })}
                        >
                          <AttendanceStatus
                            attendance={
                              sessionWithRelevantAttendance.attendance
                            }
                            pastEvent={this.state.type.past_event}
                            showSessionAtCapacityNotice={
                              showSessionAtCapacityNotice
                            }
                          />
                        </div>
                      )}

                    <FeatureFlag
                      features={['readiness-enable-multisession-events']}
                    >
                      {this.displayRsvpRejection() && (
                        <Text
                          className="fs-i mt-16 d-b"
                          variant={TEXT_VARIANTS.BODY}
                        >
                          {t(
                            'in_person_training.rsvp_status.will_not_attend_reminder',
                          )}
                        </Text>
                      )}
                    </FeatureFlag>
                  </div>
                </Card.Content>
                <Card.Content className="bg-gray-lightest bt-gray-lighter px-24 py-16">
                  <div className="Event Registration RsvpTypeAll-buttons">
                    {this.showAttendanceStatus() &&
                      this.showStatusInFooter() &&
                      sessionWithRelevantAttendance &&
                      !changingAttendance && (
                        <AttendanceStatus
                          attendance={sessionWithRelevantAttendance.attendance}
                          isRsvpTypeAll
                          pastEvent={this.state.type.past_event}
                          showSessionAtCapacityNotice={
                            sessionWithRelevantAttendance?.attendance
                              ?.rsvp_status === 'will_attend'
                              ? false
                              : this.isEventAtCapacity()
                          }
                        />
                      )}

                    {this.showAttendanceForm() &&
                      sessionWithRelevantAttendance && (
                        <AttendanceForm
                          showRsvpButtons={this.showRsvpButtons}
                          pastEvent={this.state.type.past_event}
                          handleUpdateAttendance={this.onUpdateAttendance}
                          handleChangeAttendance={this.onChangeAttendance}
                          canChangeAttendance={
                            this.state.meta.policies.can_change_attendance
                          }
                          eventAtCapacity={this.isEventAtCapacity()}
                          showAddToCalendar={this.showAddToCalendar}
                          event={event}
                          session={sessionWithRelevantAttendance}
                          attendance={attendance}
                          selectedSessionId={selectedSessionId}
                          hasMultipleSessions={this.hasMultipleSessions()}
                        />
                      )}
                  </div>
                </Card.Content>
              </Card>
            </>
          )}
        </Loading>
      </div>
    );
  }
}

export default EventRegistration;
